import { DateTime } from 'luxon';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { AccordionEditDataControl } from 'shell/view-models/edit-data-control';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import { BaseTableRows } from 'shell/components/shared/table/types';
import { ReactElement } from 'react';
import { CitizenshipVM } from 'shell/view-models/citizenship';
import { ArticleVM } from 'mutation/components/registration-citizenship/form/citizenship/form/shared/article-list';
import { AccordionFamilyCoexistence } from 'shell/view-models/accordion-family-coexistence';
import { ActManagementForm } from 'shell/view-models/act-form';
import { DatiAggiuntiviSide, ModelEventoDTO } from 'shell/models/act-category-list';

export interface BodyChangeRelationship extends BaseTableRows {
  fiscalCode?: string;
  surname?: string;
  name?: string;
  gender?: string;
  dateBorn?: string;
  placeBorn?: string;
  residence?: string;
  state?: string;
  relationship?: string | ReactElement;
  oldRelationship?: string;
  action?: ReactElement;
  idCard?: string;
}

export interface OpenLegisVM {
  dateForm?: DateTime | null;
  article?: ArticleVM | null;
}

export interface ItalianState {
  municipality?: MunicipalityAnprVM | null;
}

export interface ForeignState {
  foreignState?: ForeignStateVM | null;
  foreignProvince?: string;
}

export interface ChangeRelationshipVM {
  page?: number;
  rowsPerPage?: number;
  body?: BodyChangeRelationship[];
  count?: number;
}

export interface ToDPRVM extends ActManagementForm {
  dateBuying?: DateTime | null;
  dateDPR?: DateTime | null;
  article?: ArticleVM | null;
  dateOath?: DateTime | null;
  isForeign?: boolean;
  // italian
  italian?: ItalianState;
  // foreign
  foreign?: ForeignState;
}

export interface ChoiceItalianVM extends ActManagementForm {
  dateBuying?: DateTime | null;
  article?: ArticleVM | null;
  dateOath?: DateTime | null;
  isForeign?: boolean;
  note?: string;
  // italian
  italian?: ItalianState;
  // foreign
  foreign?: ForeignState;
}

export interface foreignCitizenshipVM extends ActManagementForm {
  dateBuying?: DateTime | null;
  otherCitizenship?: ForeignStateVM | null;
  article?: ArticleVM | null;
  dateOath?: DateTime | null;
  isForeign?: boolean;
  note?: string;
  // italian
  italian?: ItalianState;
  // foreign
  foreign?: ForeignState;
}

export interface OpeLegisVM {
  dateLoss?: DateTime | null;
  otherCitizenship?: ForeignStateVM | null;
  article?: ArticleVM | null;
  note?: string;
  changeRelationship?: ChangeRelationshipVM;
}

export interface ForeignNaturalizationVM extends ActManagementForm {
  dateLoss?: DateTime | null;
  otherCitizenship?: ForeignStateVM | null;
  article?: ArticleVM | null;
  dateOath?: DateTime | null;
  note?: string;
  isForeign?: boolean;
  changeRelationship?: ChangeRelationshipVM;
  // italian
  italian?: ItalianState;
  // foreign
  foreign?: ForeignState;
}

export interface RenunciationCitizenshipVM extends ActManagementForm {
  dateLoss?: DateTime | null;
  otherCitizenship?: ForeignStateVM | null;
  article?: ArticleVM | null;
  dateOath?: DateTime | null;
  isForeign?: boolean;
  note?: string;
  changeRelationship?: ChangeRelationshipVM;
  // italian
  italian?: ItalianState;
  // foreign
  foreign?: ForeignState;
}

export interface DoubleCitizenshipVM {
  dateFrom?: DateTime | null;
  citizenship?: ForeignStateVM | null;
  note?: string;
  article?: ArticleVM | null;
  reasonCitizenship?: string;
}

export interface ItalianCitizenshipDPVM extends ActManagementForm {
  dateFrom?: DateTime | null;
  dateDPR?: DateTime | null;
  article?: ArticleVM | null;
  note?: string;
}

export interface CitizenshipFormVM {
  reasonCitizenship?: string;
  openLegis?: OpenLegisVM;
  toDPR?: ToDPRVM;
  choiceItalian?: ChoiceItalianVM;
  foreignCitizenship?: foreignCitizenshipVM;
  opeLegis?: OpeLegisVM;
  foreignNaturalization?: ForeignNaturalizationVM;
  renunciationCitizenship?: RenunciationCitizenshipVM;
  doubleCitizenship?: DoubleCitizenshipVM;
  italianCitizenshipDP?: ItalianCitizenshipDPVM;
  idMotivoVariazioneCittadinanza?: string;
}

export const initialRegistrationCitizenship: RegistrationCitizenshipFormVM = {
  person: undefined,
  familyCoexistence: undefined,
  editDataControl: undefined,

  newCitizenship: {
    reasonCitizenship: undefined,

    openLegis: {
      dateForm: undefined,
      article: undefined,
    },

    toDPR: {
      dateBuying: undefined,
      dateDPR: undefined,
      article: undefined,
      dateOath: undefined,
      isForeign: undefined,
      italian: undefined,
      foreign: undefined,
    },

    choiceItalian: {
      dateBuying: undefined,
      article: undefined,
      dateOath: undefined,
      isForeign: undefined,
      note: undefined,
      italian: undefined,
      foreign: undefined,
    },

    foreignCitizenship: {
      dateBuying: undefined,
      otherCitizenship: undefined,
      article: undefined,
      dateOath: undefined,
      isForeign: undefined,
      note: undefined,
      italian: undefined,
      foreign: undefined,
    },

    opeLegis: {
      dateLoss: undefined,
      otherCitizenship: undefined,
      article: undefined,
      note: undefined,
      changeRelationship: {
        page: undefined,
        rowsPerPage: undefined,
        body: undefined,
        count: undefined,
      },
    },

    foreignNaturalization: {
      dateLoss: undefined,
      otherCitizenship: undefined,
      article: undefined,
      dateOath: undefined,
      note: undefined,
      isForeign: undefined,
      changeRelationship: {
        page: undefined,
        rowsPerPage: undefined,
        body: undefined,
        count: undefined,
      },
      italian: undefined,
      foreign: undefined,
    },

    renunciationCitizenship: {
      dateLoss: undefined,
      otherCitizenship: undefined,
      article: undefined,
      dateOath: undefined,
      isForeign: undefined,
      note: undefined,
      changeRelationship: {
        page: undefined,
        rowsPerPage: undefined,
        body: undefined,
        count: undefined,
      },
      italian: undefined,
      foreign: undefined,
    },

    doubleCitizenship: {
      dateFrom: undefined,
      citizenship: undefined,
      note: undefined,
      article: undefined,
      reasonCitizenship: undefined,
    },

    italianCitizenshipDP: {
      dateFrom: undefined,
      dateDPR: undefined,
      article: undefined,
      note: undefined,
    },

    idMotivoVariazioneCittadinanza: undefined,
  },

  citizenship: undefined,
  modelEventoDTO: undefined,
  datiAggiuntiviSide: undefined,
};

export interface RegistrationCitizenshipFormVM {
  // accordion person
  person?: AccordionPersonForm | null;
  familyCoexistence?: AccordionFamilyCoexistence;
  // accordion edit data control
  editDataControl?: AccordionEditDataControl;
  // accordion citizenship
  newCitizenship?: CitizenshipFormVM;
  citizenship?: Array<CitizenshipVM>;
  // utile per SC Anagrafe
  modelEventoDTO?: ModelEventoDTO;
  datiAggiuntiviSide?: DatiAggiuntiviSide;
}
